/**
 * My Orders — every meal supported and every tiffin ordered from this device
 * or account, in one list.
 *
 * Each row says which kind it is in words and colour: a green stripe and a
 * Donation tag, or a blue stripe and a Tiffin tag. The type chips narrow the
 * list to one kind; the status chips and the sort apply to both, and mean the
 * same for both — "Upcoming" is the distribution date of a donation and the
 * meal's date of a tiffin.
 *
 * The two kinds come from their own endpoints and are merged here, sorted by
 * the same rule the server sorts each by, so neither is reordered.
 */

import { useMemo, useState } from 'react';
import { FlatList, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import {
  DONATION_FILTERS,
  DONATION_SORTS,
  donationFilterLabel,
  type DonationFilter,
  type DonationSort,
  type DonationSummary,
  type OrderType,
  type TiffinOrderSummary,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { DonationListItem } from '../../components/donation/DonationListItem';
import { EmptyState, ErrorState } from '../../components/feedback/States';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { SegmentedControl } from '../../components/common/SegmentedControl';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { TiffinListItem } from '../../components/orders/TiffinListItem';
import { useAppConfig, useMyDonations, useMyTiffinOrders } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

type Kind = 'ALL' | OrderType;

const KINDS: Kind[] = ['ALL', 'DONATION', 'TIFFIN'];
const KIND_LABEL: Record<Kind, string> = { ALL: 'All', DONATION: 'Meals Supported', TIFFIN: 'Tiffin' };
const SORT_LABEL: Record<DonationSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  upcoming: 'Upcoming',
};

type Row =
  | { kind: 'DONATION'; id: string; placed: string; day: string; donation: DonationSummary }
  | { kind: 'TIFFIN'; id: string; placed: string; day: string; order: TiffinOrderSummary };

/** The server's own order for each list, applied across both. */
function compare(sort: DonationSort) {
  return (a: Row, b: Row): number => {
    if (sort === 'upcoming') return a.day.localeCompare(b.day) || b.placed.localeCompare(a.placed);
    if (sort === 'oldest') return a.placed.localeCompare(b.placed);
    return b.placed.localeCompare(a.placed);
  };
}

export function MyOrdersScreen({ navigation, route }: DonorScreenProps<'MyOrders'>) {
  const t = useT();
  const [kind, setKind] = useState<Kind>(route.params?.type ?? 'ALL');
  const [filter, setFilter] = useState<DonationFilter>('all');
  const [sort, setSort] = useState<DonationSort>('newest');

  const donations = useMyDonations(filter, sort);
  const tiffins = useMyTiffinOrders(filter, sort);
  const everyTiffin = useMyTiffinOrders();
  const { data: config } = useAppConfig();
  const tiffinOpen = Boolean(config?.tiffin?.available);
  // The type chips only mean something to someone who can order a tiffin, or
  // has.
  const showKinds = tiffinOpen || (everyTiffin.data?.total ?? 0) > 0;
  const shown: Kind = showKinds ? kind : 'DONATION';

  const rows = useMemo(() => {
    const list: Row[] = [];
    if (shown !== 'TIFFIN') {
      for (const donation of donations.data?.items ?? []) {
        list.push({
          kind: 'DONATION',
          id: donation.id,
          placed: donation.donationDate,
          day: donation.distributionDate,
          donation,
        });
      }
    }
    if (shown !== 'DONATION') {
      for (const order of tiffins.data?.items ?? []) {
        list.push({ kind: 'TIFFIN', id: order.id, placed: order.createdAt, day: order.serviceDate, order });
      }
    }
    return list.sort(compare(sort));
  }, [donations.data, tiffins.data, shown, sort]);

  const loading =
    (shown !== 'TIFFIN' && donations.isLoading) || (shown !== 'DONATION' && tiffins.isLoading);
  const failed =
    (shown !== 'TIFFIN' && donations.isError) || (shown !== 'DONATION' && tiffins.isError);
  const refetch = () => {
    void donations.refetch();
    void tiffins.refetch();
  };

  const emptyTitle =
    filter !== 'all'
      ? `${t('No orders')} — ${t(donationFilterLabel[filter])}`
      : shown === 'TIFFIN'
        ? t('No tiffin orders yet')
        : shown === 'DONATION'
          ? t('No supported meals yet')
          : t('No orders yet');
  const emptyAction =
    filter !== 'all'
      ? null
      : shown === 'TIFFIN'
        ? tiffinOpen
          ? { label: t('Order a Tiffin'), go: () => navigation.navigate('TiffinMenu') }
          : null
        : { label: t('Support a Meal'), go: () => navigation.navigate('DonateMeal') };

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      scrollable={false}
      onRefresh={refetch}
      refreshing={donations.isRefetching || tiffins.isRefetching}
      testID="screen-my-orders"
      title={t('My Orders')}
      subtitle={showKinds ? t('Your supported meals and tiffin orders') : t('Meals you have supported')}
    >
      {showKinds ? (
        <SegmentedControl
          options={KINDS}
          value={kind}
          onChange={setKind}
          label={(option) => t(KIND_LABEL[option])}
          testIDPrefix="type"
          style={styles.kinds}
        />
      ) : null}

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        // Without flexGrow:0 a horizontal ScrollView inside a column flex
        // container has no intrinsic height and stretches to fill the screen,
        // turning the filter chips into full-height bars.
        style={styles.chipStrip}
        contentContainerStyle={styles.chips}
        accessibilityRole="tablist"
      >
        {DONATION_FILTERS.map((option) => {
          const active = option === filter;
          return (
            <Pressable
              key={option}
              testID={`filter-${option}`}
              onPress={() => setFilter(option)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              style={[styles.chip, active && styles.chipActive]}
            >
              <Text variant="label" tone={active ? 'onBrand' : 'secondary'}>
                {t(donationFilterLabel[option])}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      <View style={styles.sortRow}>
        <Text variant="caption" tone="tertiary">{t('Sort by')}</Text>
        {DONATION_SORTS.map((option) => (
          <Pressable
            key={option}
            testID={`sort-${option}`}
            onPress={() => setSort(option)}
            accessibilityRole="button"
            accessibilityState={{ selected: option === sort }}
          >
            <Text variant="caption" tone={option === sort ? 'brand' : 'tertiary'}>
              {t(SORT_LABEL[option])}
            </Text>
          </Pressable>
        ))}
      </View>

      {loading ? (
        <View style={styles.loading} testID="orders-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={140} />
          ))}
        </View>
      ) : failed ? (
        <ErrorState title={t('We could not load your orders')} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          message={
            filter !== 'all'
              ? t('Try a different filter to see your other orders.')
              : t('Meals you support and tiffins you order from this device appear here, with their progress.')
          }
          actionLabel={emptyAction?.label}
          onAction={emptyAction?.go}
        />
      ) : (
        <FlatList
          data={rows}
          keyExtractor={(row) => `${row.kind}-${row.id}`}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          testID="my-orders"
          renderItem={({ item }) =>
            item.kind === 'DONATION' ? (
              <DonationListItem
                donation={item.donation}
                onPress={(donation) => navigation.navigate('Tracking', { donationId: donation.id })}
              />
            ) : (
              <TiffinListItem
                order={item.order}
                onPress={(order) => navigation.navigate('TiffinOrder', { orderId: order.id })}
              />
            )
          }
        />
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  kinds: {
    marginBottom: space.sm,
  },
  chipStrip: {
    flexGrow: 0,
    flexShrink: 0,
  },
  chips: {
    gap: space.sm,
    alignItems: 'center',
    paddingVertical: space.xs,
  },
  chip: {
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
    // Hug the label; the strip above decides the row height.
    alignSelf: 'center',
  },
  chipActive: {
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.brand,
  },
  sortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: space.md,
    paddingTop: space.md,
    paddingBottom: space.md,
  },
  loading: {
    gap: space.md,
  },
  list: {
    flex: 1,
  },
  listContent: {
    gap: space.md,
    paddingBottom: space.xl,
  },
});
